import { ApolloCache } from '@apollo/client';
import { useRouteMatch } from 'react-router-dom';

import {
  ProductLikesFragmentDoc,
  ProductDetailsLikesFragmentDoc,
  ProductLikesFragment,
  ProductDetailsLikesFragment,
  ProductDetailsProductsLikesFragment,
  ProductDetailsProductsLikesFragmentDoc,
} from '@models';
import {
  ecommerceTrigger,
  sendAddToFavoritesEvent,
  sendOnLikeEvent,
  sendRTBHouseFavoriteEvent,
} from '@utils/analytics';
import { getProductCollectionSlug, getProductPageReferrerFromStorage, useCollectionRouteMatch } from '@utils/common';
import { productDetails } from '@legacyUrls';
import { ClerkActionType, ClerkAddToFavouriteEvent, ClerkCategoryType, useClerk } from '@hook/useClerk';

import { fbqEventAfterLike } from '../../analytics';

import {
  AddToFavouritesMutation,
  RemoveFromFavouritesMutation,
  useRemoveFromFavouritesMutation,
  useAddToFavouritesMutation,
} from './gql/graphql.v3.generated';

const optimisticAddFavouriteResponse: AddToFavouritesMutation = {
  addToFavourites: {
    isSuccess: true,
    __typename: 'AddFavourites',
  },
};
const optimisticRemoveFavouriteResponse: RemoveFromFavouritesMutation = {
  removeFromFavourites: {
    isSuccess: true,
    __typename: 'RemoveFavourites',
  },
};

export interface Product {
  id: number,
  name: string,
  price?: number | null,
  breadcrumbs?: { id: number, title: string }[] | string,
  catalog?: { slug: string } | null
  brand?: { name: string } | null
  likes: number,
  isLiked: boolean,
}

export type ProductFavoriteType = 'Product' | 'ProductDetails' | 'ProductDetailsProducts';

interface Payload {
  product: Product,
  type: ProductFavoriteType,
}

export const useProductFavorite = ({
  product,
  type,
}: Payload) => {
  const collectionMatch = useCollectionRouteMatch();
  const productDetailsMatch = useRouteMatch(productDetails);

  const { sendClerkEvent } = useClerk();

  const fragmentId = `${type}:${product.id}`;
  let fragment = ProductLikesFragmentDoc;

  if (type === 'ProductDetails') {
    fragment = ProductDetailsLikesFragmentDoc;
  } else if (type === 'ProductDetailsProducts') {
    fragment = ProductDetailsProductsLikesFragmentDoc;
  }

  const handleUpdateCache = (
    cache: ApolloCache<unknown>,
    {
      data,
    } : {
      data?: RemoveFromFavouritesMutation | AddToFavouritesMutation | null,
    },
  ) => {
    const cacheData = cache.readFragment<
    ProductLikesFragment
    | ProductDetailsLikesFragment
    | ProductDetailsProductsLikesFragment
    >({
      id: `${type}:${product.id}`,
      fragment,
    });

    let likesNew = cacheData?.likes || 0;
    let isLikedNew = !!cacheData?.isLiked;

    if (data && 'addToFavourites' in data) {
      likesNew += 1;
      isLikedNew = true;
    } else {
      likesNew -= 1;
      isLikedNew = false;
    }

    cache.writeFragment({
      id: fragmentId,
      fragment,
      data: {
        isLiked: isLikedNew,
        likes: likesNew,
        __typename: type,
      },
    });
  };

  const [addToFavourites] = useAddToFavouritesMutation({
    optimisticResponse: optimisticAddFavouriteResponse,
    update: handleUpdateCache,
    onCompleted: () => {
      const storageReferrer = getProductPageReferrerFromStorage(product.id);

      if (product.breadcrumbs) {
        fbqEventAfterLike(
          product.name,
          product.breadcrumbs,
          product.id,
          product.price,
        );
      }

      sendClerkEvent<ClerkAddToFavouriteEvent>({
        category: ClerkCategoryType.addToFavourite,
        action: ClerkActionType.click,
        payload: {
          itemCount: String(product.likes),
          ...(productDetailsMatch && storageReferrer && {
            referrer: storageReferrer,
          }),
        },
      });

      sendAddToFavoritesEvent({
        collection: collectionMatch
          ? collectionMatch.params.slug
          : getProductCollectionSlug(product.id),
        ...(productDetailsMatch && storageReferrer && {
          referrer: storageReferrer,
        }),
      });
      sendRTBHouseFavoriteEvent(String(product.id));
      sendOnLikeEvent(product.id, product.price);
      ecommerceTrigger({
        event: 'add_to_wishlist',
        products: [{
          ...product,
        }],
      });
    },
  });

  const [removeFromFavourites] = useRemoveFromFavouritesMutation({
    optimisticResponse: optimisticRemoveFavouriteResponse,
    update: handleUpdateCache,
  });

  const handleAddToFavorites = () => addToFavourites({
    variables: {
      ids: [product.id],
    },
  });

  const handleRemoveFromFavorites = () => removeFromFavourites({
    variables: {
      ids: [product.id],
    },
  });

  return {
    handleAddToFavorites,
    handleRemoveFromFavorites,
  };
};
